import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "264",
  "slug": "geography-skill-tier-list",
  "title": "The Geography Skill Tier List: From Casual to Rainbolt-Level",
  "excerpt": "From the adult who can name maybe fifty countries to the player who identifies Bulgaria from a road sign, here is the unofficial tier list for geography skill — and an honest test of where you sit.",
  "date": "2026-05-02",
  "category": "Community",
  "image": "https://images.unsplash.com/photo-1526778548025-fa2f459cd5c1?w=800&q=80",
  "readTime": "5 min",
  "content": [
    {
      "type": "paragraph",
      "text": "Tier lists are a guilty pleasure of the internet. Originally a fighting-game community tradition for ranking characters, they have spread to almost every domain where people care about comparative skill. Geography skill, it turns out, lends itself to the format unusually well — partly because the skill is multidimensional, partly because the gap between casual and expert is comically large, and partly because every player can immediately self-locate on the list with reasonable accuracy."
    },
    {
      "type": "paragraph",
      "text": "Here is the tier list, ranked from \"average adult\" to \"top global player.\" Find your level, then find out what it takes to move up."
    },
    {
      "type": "heading",
      "text": "F Tier — The Casual Map-Glancer"
    },
    {
      "type": "paragraph",
      "text": "Can name roughly 30-50 countries when asked. Can point to about half of those on an unlabelled map. Knows where their home country is, the major neighbours, and a handful of \"famous\" countries. Confuses Austria with Australia at least once a year. Has no idea where Kazakhstan, Lesotho, or Suriname are. Has never heard of Eswatini."
    },
    {
      "type": "paragraph",
      "text": "This is the average adult, globally. There is no shame in starting here. The majority of the world's population sits in F tier through no fault of their own — geography education is patchy, and most people have not had any reason to actively maintain their geographic knowledge since secondary school. Movement out of F tier requires only a few weeks of casual exposure to any geography game or app."
    },
    {
      "type": "heading",
      "text": "D Tier — The Travel-Reader"
    },
    {
      "type": "paragraph",
      "text": "Names 80-100 countries. Can point to most of them on a map. Knows the rough shape of all continents and the major countries within each. Can identify roughly two-thirds of country flags. Has a working sense of where in the world the Sahara, the Andes, the Himalayas, and the Amazon are. Still confuses several pairs of similar-sounding African countries."
    },
    {
      "type": "paragraph",
      "text": "D tier is the level of someone who reads travel writing, watches international news, and has been to a few different countries themselves. It is the natural plateau for engaged general-knowledge adults who do not specifically practice geography. Most readers of this article are probably in D tier or solidly into C tier."
    },
    {
      "type": "heading",
      "text": "C Tier — The Casual Geography Player"
    },
    {
      "type": "paragraph",
      "text": "Names 130-160 countries. Recognises most country shapes by silhouette. Can name most capitals of larger countries, though Caribbean and Pacific capitals are shaky. Plays one or more daily geography games. Has put some hours into satellite-imagery or street-view games and can place rounds within roughly the right continent most of the time. Knows the difference between the Gobi and the Empty Quarter, between Bolivia and Paraguay."
    },
    {
      "type": "paragraph",
      "text": "C tier is the level of someone who has actively spent some time on geography as a hobby. The skill is genuine and substantially above adult average, but the gaps are still real — in pacific islands, in former Soviet states, in central Africa."
    },
    {
      "type": "heading",
      "text": "B Tier — The Engaged Hobbyist"
    },
    {
      "type": "paragraph",
      "text": "Names all 195 countries. Knows nearly all capitals. Can place most country shapes on a blank map within a small margin of error. Plays satellite imagery games to a level where most rounds land within a few hundred kilometres of the correct answer. Has favourite countries to play (rare ones, distinctive ones) and has cultivated specific regional expertise (\"I am really good at the Caucasus\")."
    },
    {
      "type": "paragraph",
      "text": "B tier is where most active geography hobbyists end up. The skill is well-developed but still has clear ceilings — you can place most rounds correctly, but not within ten kilometres; you can identify most flags, but a few obscure ones still surprise you."
    },
    {
      "type": "heading",
      "text": "A Tier — The Competitive Player"
    },
    {
      "type": "paragraph",
      "text": "Plays competitive multiplayer geography regularly. Can identify subtle regional cues — soil colour, road-line style, vegetation species — and use them to narrow location dramatically. Scores in the top few percent of public daily challenges. Has hundreds of hours of play. Can routinely place rounds within fifty kilometres anywhere in the world."
    },
    {
      "type": "paragraph",
      "text": "A tier is the level of serious competitive players who treat geography games as a hobby with depth. Most A tier players have detailed mental maps of how specific countries differ in landscape signatures from their neighbours. They can talk about the specific look of Eastern Bulgarian villages versus Romanian ones in a way that no F tier player would consider possible."
    },
    {
      "type": "heading",
      "text": "S Tier — The Streamer-Tier Pro"
    },
    {
      "type": "paragraph",
      "text": "Names every country. Knows every capital. Recognises road-sign styles by country. Identifies utility-pole shapes, guardrail patterns, and license-plate designs by region. Has memorised the specific look of rural fences in dozens of countries. Can place a round within ten kilometres anywhere on Earth, frequently within one. Plays competitively at the top of public leaderboards."
    },
    {
      "type": "paragraph",
      "text": "S tier is the level of Rainbolt, GeoWizard, and the other top streamers and competitive players. The skill is genuinely extraordinary — these players have spent thousands of hours specifically building it, and the result is closer to a professional craft than a casual hobby. There are perhaps a few hundred S-tier geography players in the world."
    },
    {
      "type": "heading",
      "text": "How to Move Up the Tier List"
    },
    {
      "type": "paragraph",
      "text": "The encouraging thing about the tier list is that movement is fast. F to D takes about a month of daily play. D to C takes another two or three months. C to B takes a year of consistent play. B to A takes a few thousand hours. A to S takes deliberate study and competitive practice on top of that."
    },
    {
      "type": "paragraph",
      "text": "Most players plateau at C or B and are perfectly happy there — the skill is substantial, the time commitment is reasonable, and the rounds are still genuinely fun to play. A and S tier require a real investment, the same way reaching grandmaster in chess does. Whether the investment is worth it depends on how much you enjoy the practice. For the people who do — and there are more of them than you might think — geography play becomes one of the few hobbies where the skill keeps growing for years."
    }
  ]
};
